/* istanbul ignore file */
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { CriteoProductsRecoSlider, PrudsysAddToBasketRecoSlider } from '@packages/recommendations';
import { Box, Button, Card, CardContent, ErrorBoundary } from '@packages/shared';
import type { FC } from 'react';
import { Suspense } from 'react';
import { useIntl } from 'react-intl';
import { useHashParameters } from '../../hooks/useHashParameters';
import { AddedProduct } from './AddedProduct';
import { addToBasketLayerMessages } from './messages';
import { AddedProductLoadingFallback } from './AddedProduct/AddedProductLoadingFallback';

const AddToBasketLayerFragment = graphql(/* GraphQL */ `
  fragment AddToBasketLayerFragment on Product {
    availability {
      state
    }
    brand {
      name
    }
    defaultCategory
    delivery {
      ...TaxHintFragment
    }
    hasSeries
    orderNumber
    price {
      value
    }
  }
`);

type AddToBasketLayerProps = {
  lineItems?: Array<string>;
  data: FragmentType<typeof AddToBasketLayerFragment>;
};

export const AddToBasketLayer: FC<AddToBasketLayerProps> = ({ lineItems, data }) => {
  const { brand, defaultCategory, orderNumber, hasSeries, price, availability, delivery } =
    getFragmentData(AddToBasketLayerFragment, data);

  const { category = '' } = useHashParameters();
  const { formatMessage } = useIntl();

  const errorMessages = {
    text: formatMessage(addToBasketLayerMessages.errorMessage),
    button: formatMessage(addToBasketLayerMessages.errorButtonText),
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: [1.5, 1.5, 3],
        bgcolor: 'grey.light',
      }}
    >
      <Card>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <ErrorBoundary
            fallback={(reset) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {`${errorMessages.text} `}
                <Button onClick={reset}>{errorMessages.button}</Button>
              </Box>
            )}
          >
            <Suspense fallback={<AddedProductLoadingFallback />}>
              <AddedProduct lineItems={lineItems} taxHintData={delivery} />
            </Suspense>
          </ErrorBoundary>
        </CardContent>
      </Card>

      <PrudsysAddToBasketRecoSlider
        brand={brand?.name ?? ''}
        categoryIds={[defaultCategory ?? category]}
        productId={orderNumber}
        elementIndex={hasSeries ? 4 : 3}
        pageTemplate="detailview"
        title={formatMessage(addToBasketLayerMessages.prudsysFallback)}
      />

      <CriteoProductsRecoSlider
        available={availability.state === 'NOT_AVAILABLE' ? 0 : 1}
        price={parseFloat(price.value.toFixed(2))}
        productId={orderNumber}
        fetchOnThreshold
        elementIndex={hasSeries ? 2 : 1}
        pageTemplate="detailview"
        title={formatMessage(addToBasketLayerMessages.criteoFallback)}
        key={`${orderNumber}${2}`}
      />
    </Box>
  );
};
